import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
let facetsURL = window.location.href;
let filterRequest = 0;
window.addEventListener('popstate', () => {
  if (window.location.href === facetsURL) return;
  facetsURL = window.location.href;
  document.querySelector('facets-form-component')?.updateFiltersByURL(facetsURL, false);
});

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    const sizeParam = this.dataset.stockSizeParam;
    if (sizeParam && newParameters.getAll(sizeParam).some(Boolean)) {
      newParameters.set('filter.v.availability', '1');
    }

    newParameters.delete('page');
    // Both desktop radios and the mobile select belong to the form.
    const sortControls = Array.from(this.refs.facetsForm.elements).filter((input) =>
      input.name === 'sort_by' && !input.disabled &&
      (input instanceof HTMLSelectElement || input.checked));
    const visibleSort = sortControls.find((input) => input.getClientRects().length &&
      getComputedStyle(input).visibility !== 'hidden');
    const sort = visibleSort?.value || newParameters.get('sort_by');
    if (sort) newParameters.set('sort_by', sort);

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  updateFilters = () => {
    const url = new URL(window.location.href);
    url.search = this.createURLParameters().toString();
    this.updateFiltersByURL(url.toString());
  };

  async updateFiltersByURL(value, pushHistory = true) {
    const url = new URL(value, window.location.origin);
    const sizeParam = this.dataset.stockSizeParam;
    if (sizeParam && url.searchParams.getAll(sizeParam).some(Boolean)) {
      url.searchParams.set('filter.v.availability', '1');
    }
    const request = ++filterRequest;
    if (pushHistory && url.href !== window.location.href) {
      history.pushState({ ...history.state, facets: true }, '', url);
    }
    facetsURL = url.href;
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    section?.setAttribute('aria-busy', 'true');
    this.dispatchEvent(new FilterUpdateEvent(url.searchParams));
    try {
      await sectionRenderer.renderSection(this.sectionId, { url: new URL(url) });
      if (request !== filterRequest) return;
      section?.querySelectorAll('price-range-slider').forEach((slider) => slider.sync());
      section?.dispatchEvent(new CustomEvent('facets:rendered', { bubbles: true }));
    } catch (error) {
      if (request === filterRequest) window.location.assign(url.href);
    } finally {
      if (request === filterRequest) section?.removeAttribute('aria-busy');
    }
  }

}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url).catch(() => {});
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');

    requestAnimationFrame(() => {
      if (this.refs.minSlider && this.refs.maxSlider) {
        this.#initSliders();
      }
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  #onKeyDown = (event) => {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  // ----- Slider logic -----

  #initSliders() {
    const { minSlider, maxSlider, minInput, maxInput, sliderFill } = this.refs;

    this.#syncSliderFromInput('min');
    this.#syncSliderFromInput('max');

  this.#updateFill();

    this.#updateFill();

    minSlider.addEventListener('input', () => {
      const minVal = parseInt(minSlider.value);
      const maxVal = parseInt(maxSlider.value);
      if (minVal > maxVal) minSlider.value = maxSlider.value;
      this.#syncInputFromSlider('min');
      this.#updateFill();
    });

    maxSlider.addEventListener('input', () => {
      const minVal = parseInt(minSlider.value);
      const maxVal = parseInt(maxSlider.value);
      if (maxVal < minVal) maxSlider.value = minSlider.value;
      this.#syncInputFromSlider('max');
      this.#updateFill();
    });

    // Al soltar el slider, disparar el filtro
    minSlider.addEventListener('change', () => this.updatePriceFilterAndResults());
    maxSlider.addEventListener('change', () => this.updatePriceFilterAndResults());

    // Sincronizar sliders cuando el usuario escribe en los inputs
    minInput?.addEventListener('change', () => this.#syncSliderFromInput('min'));
    maxInput?.addEventListener('change', () => this.#syncSliderFromInput('max'));
  }

  #syncInputFromSlider(which) {
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    const rangeMax = parseFloat(minSlider.max);

    if (which === 'min') {
      const rawVal = parseFloat(minSlider.value);
      // Si el slider está en 0, limpiar el input (sin filtro)
      minInput.value = rawVal === 0 ? '' : formatMoney(rawVal * 100, this.moneyFormat, this.currency);
    } else {
      const rawVal = parseFloat(maxSlider.value);
      // Si el slider está al máximo, limpiar el input (sin filtro)
      maxInput.value = rawVal >= rangeMax ? '' : formatMoney(rawVal * 100, this.moneyFormat, this.currency);
    }
  }

  #syncSliderFromInput(which) {
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;

    if (which === 'min') {
      const raw = minInput.value.trim();
      if (!raw) {
        minSlider.value = minSlider.min || '0';
      } else {
        const val = convertMoneyToMinorUnits(raw, this.currency);
        if (val !== null) minSlider.value = String(val / 100);
      }
    } else {
      const raw = maxInput.value.trim();
      if (!raw) {
        maxSlider.value = maxSlider.max;
      } else {
        const val = convertMoneyToMinorUnits(raw, this.currency);
        if (val !== null) maxSlider.value = String(val / 100);
      }
    }

    this.#updateFill();
  }

  #updateFill() {
    const { minSlider, maxSlider, sliderFill } = this.refs;
    if (!sliderFill) return;

    const min = parseFloat(minSlider.min) || 0;
    const max = parseFloat(minSlider.max) || 100;
    const from = ((parseFloat(minSlider.value) - min) / (max - min)) * 100;
    const to = ((parseFloat(maxSlider.value) - min) / (max - min)) * 100;

    sliderFill.style.left = from + '%';
    sliderFill.style.width = (to - from) + '%';
  }

  // ----- Lógica original de Horizon (sin cambios) -----

  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    const value = this.#parseDisplayValue(input.value, currency);
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');
    if (!(statusComponent instanceof FacetStatusComponent)) return;
    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}





if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}


class PriceRangeSlider extends HTMLElement {
  #events;
  connectedCallback() {
    this.#events = new AbortController();
    this.addEventListener('input', this.#input, { signal: this.#events.signal });
    this.addEventListener('change', this.#change, { signal: this.#events.signal });
    requestAnimationFrame(() => { if (this.isConnected) this.sync(); });
  }
  disconnectedCallback() { this.#events?.abort(); }
  sync() {
    this.querySelector('.price-range-slider__input--min').value = this.dataset.currentMin;
    this.querySelector('.price-range-slider__input--max').value = this.dataset.currentMax;
    this.#paint();
  }
  #input = (event) => {
    if (!event.target.matches('input[type="range"]')) return;
    const min = this.querySelector('.price-range-slider__input--min');
    const max = this.querySelector('.price-range-slider__input--max');
    if (+min.value > +max.value) event.target.value = event.target === min ? max.value : min.value;
    this.#paint();
  };
  #change = (event) => {
    if (!event.target.matches('input[type="range"]')) return;
    this.#paint();
    this.closest('facets-form-component')?.updateFilters();
  };
  #paint() {
    const min = this.querySelector('.price-range-slider__input--min');
    const max = this.querySelector('.price-range-slider__input--max');
    const floor = +this.dataset.min, ceiling = +this.dataset.max, range = ceiling - floor;
    const money = new Intl.NumberFormat(document.documentElement.lang === 'es' ? 'es-AR' : document.documentElement.lang || 'es-AR', {
      style: 'currency', currency: this.dataset.currency || 'ARS', maximumFractionDigits: 0,
    });
    this.querySelector('.price-range-slider__label--min').textContent = money.format(+min.value / 100);
    this.querySelector('.price-range-slider__label--max').textContent = money.format(+max.value / 100);
    const fill = this.querySelector('.price-range-slider__fill');
    fill.style.left = `${range ? (+min.value - floor) / range * 100 : 0}%`;
    fill.style.right = `${range ? (ceiling - +max.value) / range * 100 : 0}%`;
    min.style.zIndex = +min.value >= +max.value ? '4' : '';
    this.querySelector('[data-price-min]').value = +min.value > floor ? +min.value / 100 : '';
    this.querySelector('[data-price-max]').value = +max.value < ceiling ? +max.value / 100 : '';
    min.setAttribute('aria-valuetext', money.format(+min.value / 100));
    max.setAttribute('aria-valuetext', money.format(+max.value / 100));
  }
}
if (!customElements.get('price-range-slider')) customElements.define('price-range-slider', PriceRangeSlider);

// Collection toolbar persists its state through native section rendering.
let collectionFiltersCollapsed = false;
const collectionDesktopQuery = window.matchMedia('(min-width: 750px)');
const syncCollectionFilters = () => {
  const collection = document.querySelector('body.template-collection results-list');
  if (!collection) return;
  collection.toggleAttribute('data-filters-collapsed', collectionFiltersCollapsed);
  collection.querySelector('.iurop-filter-toggle')?.setAttribute('aria-expanded', String(!collectionFiltersCollapsed));
  const panel = collection.querySelector('.collection-wrapper > .facets-block-wrapper:not(#filters-drawer)');
  if (panel) panel.inert = collectionFiltersCollapsed && collectionDesktopQuery.matches;
};
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.iurop-filter-toggle') : null;
  if (!button) return;
  collectionFiltersCollapsed = !collectionFiltersCollapsed;
  syncCollectionFilters();
});
document.addEventListener('facets:rendered', syncCollectionFilters);
collectionDesktopQuery.addEventListener('change', syncCollectionFilters);
const normalizeCollectionSizeStock = () => {
  const form = document.querySelector('body.template-collection facets-form-component[data-stock-size-param]');
  const sizeParam = form?.dataset.stockSizeParam;
  const url = new URL(window.location.href);
  const sizeNeedsStock = sizeParam && url.searchParams.getAll(sizeParam).some(Boolean) && url.searchParams.get('filter.v.availability') !== '1';
  if (form && (!url.searchParams.has('filter.v.availability') || sizeNeedsStock)) {
    url.searchParams.set('filter.v.availability', '1');
    history.replaceState(history.state, '', url);
    form.updateFiltersByURL(url.href, false);
  }
};
normalizeCollectionSizeStock();
